import { api } from "../api";

export type VariationType = "string" | "number";
export type VariationInputMode = "selector";

export interface VariationValue {
  id: string;
  value: string;
  label: string;
  sortOrder: number | null;
  isCustom: boolean;
}

export interface GlobalVariation {
  id: string;
  label: string;
  description: string | null;
  type: VariationType;
  inputMode: VariationInputMode;
  unit: string | null;
  decimalPlaces: number | null;
  isCustom: false;
  isActive: boolean;
}

export interface GlobalVariationDetail extends GlobalVariation {
  categories: Array<{ categoryId: string; subcategoryId: string | null }>;
}

export interface SyncGlobalVariationData {
  label: string;
  description?: string | null;
  type: VariationType;
  inputMode: VariationInputMode;
  unit?: string | null;
  decimalPlaces?: number | null;
  values?: Array<{
    id?: string;
    label: string;
    sortOrder?: number | null;
  }>;
  /**
   * Where the variation is offered. Sent: replaces the current links (the
   * ones left out are deactivated). Omitted: the links stay as they are.
   */
  categories?: Array<{ categoryId: string; subcategoryId?: string | null }>;
}

export interface CreateGlobalVariationData {
  label: string;
  description?: string;
  type: VariationType;
  inputMode: VariationInputMode;
  unit?: string | null;
  decimalPlaces?: number | null;
  values?: Array<{ label: string; sortOrder?: number }>;
  categories?: Array<{ categoryId: string; subcategoryId?: string | null }>;
}

const basePath = "/v2/admin/variations";

export async function listarVariacoesGlobais(): Promise<GlobalVariation[]> {
  const response = await api.get<GlobalVariation[]>(basePath);
  return response.data;
}

export async function listarValoresVariacaoGlobal(
  variationId: string,
): Promise<VariationValue[]> {
  const response = await api.get<VariationValue[]>(
    `${basePath}/${variationId}/values`,
  );
  return response.data;
}

export async function criarVariacaoGlobal(
  data: CreateGlobalVariationData,
): Promise<GlobalVariation> {
  const response = await api.post<GlobalVariation>(basePath, data);
  return response.data;
}

export async function buscarVariacaoGlobal(
  id: string,
): Promise<GlobalVariationDetail> {
  const response = await api.get<GlobalVariationDetail>(`${basePath}/${id}`);
  return response.data;
}

export async function atualizarVariacaoGlobal(
  id: string,
  data: Partial<
    Pick<
      CreateGlobalVariationData,
      "label" | "description" | "inputMode" | "unit" | "decimalPlaces"
    >
  >,
): Promise<GlobalVariationDetail> {
  const response = await api.patch<GlobalVariationDetail>(
    `${basePath}/${id}`,
    data,
  );
  return response.data;
}

export async function sincronizarVariacaoGlobal(
  id: string,
  data: SyncGlobalVariationData,
): Promise<void> {
  await api.put(`${basePath}/${id}`, data);
}

export async function desativarVariacaoGlobal(id: string): Promise<void> {
  await api.delete(`${basePath}/${id}`);
}

export async function criarValorVariacaoGlobal(
  variationId: string,
  data: { label: string; sortOrder?: number | null },
): Promise<VariationValue> {
  const response = await api.post<VariationValue>(
    `${basePath}/${variationId}/values`,
    data,
  );
  return response.data;
}

export async function atualizarValorVariacaoGlobal(
  variationId: string,
  valueId: string,
  data: Partial<{ label: string; sortOrder: number | null }>,
): Promise<VariationValue> {
  const response = await api.patch<VariationValue>(
    `${basePath}/${variationId}/values/${valueId}`,
    data,
  );
  return response.data;
}

export async function desativarValorVariacaoGlobal(
  variationId: string,
  valueId: string,
): Promise<void> {
  await api.delete(`${basePath}/${variationId}/values/${valueId}`);
}
